import {
  AnimatePresence,
  MotionConfig,
  motion,
  type Variants,
} from "motion/react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import useMeasure from "react-use-measure";
import { useOnClickOutside } from "usehooks-ts";
import type { MsiOptions } from "@/lib/schemas/paycode/msi-options";
import { useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { HeroIcon } from "../hero-icon";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

type MsiPickerProps = {
  options: MsiOptions;
  value: number;
  onChange: (value: number) => void;
};

const installmentConfig: Record<keyof MsiOptions, { months: number }> = {
  threeMonths: { months: 3 },
  sixMonths: { months: 6 },
  nineMonths: { months: 9 },
  twelveMonths: { months: 12 },
  eighteenMonths: { months: 18 },
};

const variants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transform: "translateY(0px)" },
  exit: { opacity: 0 },
};

const TRANSITION_DURATION = 0.2;

export const MsiPicker = ({ options, value, onChange }: MsiPickerProps) => {
  const t = useTranslations("installments");
  const total = useCheckoutStore((s) => s.total);
  const [expanded, setExpanded] = useState(false);

  const [elementRef, bounds] = useMeasure();
  const containerRef = useRef<HTMLDivElement>(null);

  const expand = () => setExpanded(true);

  const handleChange = (next: string) => {
    setExpanded(false);
    onChange(Number(next));
  };

  const onClickOutside = () => setExpanded(false);

  const availableInstallments = (
    Object.keys(installmentConfig) as (keyof MsiOptions)[]
  ).filter((key) => options[key]);

  const selectedEntry = (
    Object.entries(installmentConfig) as [
      keyof MsiOptions,
      (typeof installmentConfig)[keyof MsiOptions],
    ][]
  ).find(([, config]) => config.months === value);

  const selectedInstallment = selectedEntry
    ? {
        key: selectedEntry[0],
        months: selectedEntry[1].months,
      }
    : { key: "single-payment" as const, months: 0 };

  const selectedLabel =
    selectedInstallment.months === 0
      ? t("singlePayment")
      : t("monthsNoInterest", { months: selectedInstallment.months });

  const selectedPerMonth =
    selectedInstallment.months === 0
      ? total
      : total / selectedInstallment.months;

  // @ts-expect-error
  useOnClickOutside(containerRef, onClickOutside);

  if (availableInstallments.length === 0) return null;

  return (
    <motion.div
      ref={containerRef}
      animate={{
        height: bounds.height ?? 56,
        transition: {
          duration: TRANSITION_DURATION,
          ease: [0.25, 1, 0.5, 1],
        },
      }}
      className="min-h-14 rounded-lg border border-border bg-background"
    >
      <div ref={elementRef}>
        <MotionConfig
          transition={{
            duration: TRANSITION_DURATION,
            ease: [0.26, 0.08, 0.25, 1],
          }}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {!expanded ? (
              <motion.button
                key="msi-picker-collapsed"
                type="button"
                className="flex h-14 w-full cursor-pointer flex-col justify-center px-4 text-start"
                onClick={expand}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={variants}
              >
                <span className="text-muted text-xs">{t("header")}</span>
                <div className="flex flex-row items-center gap-1.5">
                  <motion.p
                    layout="position"
                    layoutId={`label-${selectedInstallment.key}`}
                  >
                    {selectedLabel}
                  </motion.p>
                  <span className="text-muted text-sm">·</span>
                  <motion.p
                    className="text-muted text-sm"
                    layout="position"
                    layoutId={`amount-${selectedInstallment.key}`}
                  >
                    {selectedPerMonth.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                    {selectedInstallment.months > 0 && ` ${t("perMonth")}`}
                  </motion.p>
                </div>
                <motion.div
                  className="absolute right-4"
                  exit={{ opacity: 0, transition: { duration: 0 } }}
                >
                  <HeroIcon
                    name="chevron-down"
                    iconStyle="mini"
                    className="h-4 w-4"
                  />
                </motion.div>
              </motion.button>
            ) : (
              <motion.div
                key="msi-picker-expanded"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={variants}
                className="px-4 py-2 pb-4"
              >
                <RadioGroup value={String(value)} onValueChange={handleChange}>
                  <span className="text-muted text-xs">
                    {t("selectOption")}
                  </span>
                  <label
                    htmlFor="single-payment"
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-lg border border-border p-2",
                      value === 0 && "border-accent",
                    )}
                  >
                    <RadioGroupItem value="0" id="single-payment" />
                    <div className="flex flex-col">
                      <motion.span
                        layout="position"
                        layoutId="label-single-payment"
                        className="text-sm"
                      >
                        {t("singlePayment")}
                      </motion.span>
                      <motion.span
                        layout="position"
                        layoutId="amount-single-payment"
                        className="text-muted text-xs"
                      >
                        {total.toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        })}
                      </motion.span>
                    </div>
                  </label>
                  {availableInstallments.map((key) => {
                    const { months } = installmentConfig[key];
                    const id = `msi-${months}`;
                    const perMonth = total / months;
                    return (
                      <label
                        htmlFor={id}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg border border-border p-2",
                          value === months && "border-accent",
                        )}
                        key={key}
                      >
                        <RadioGroupItem value={String(months)} id={id} />
                        <div className="flex flex-col">
                          <motion.span
                            layout="position"
                            layoutId={`label-${key}`}
                            className="font-regular text-sm"
                          >
                            {t("monthsNoInterest", { months })}
                          </motion.span>
                          <motion.span
                            layout="position"
                            layoutId={`amount-${key}`}
                            className="text-muted text-xs"
                          >
                            {perMonth.toLocaleString("es-MX", {
                              style: "currency",
                              currency: "MXN",
                            })}{" "}
                            {t("perMonth")}
                          </motion.span>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </motion.div>
            )}
          </AnimatePresence>
        </MotionConfig>
      </div>
    </motion.div>
  );
};
